import { Text, UI, HStack, VStack, Stack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from 'native-base';
import React, { useEffect } from 'react';
import { Control, useFormContext, useWatch, useForm, FormProvider } from 'react-hook-form';
import * as zod from 'zod';

import { LabelTypes, labelTypesMask } from './constants';

export declare namespace RangeTypes {
  interface RangeProps {
    min: number;
    max: number;
  }

  type ValueChange = (range: RangeProps, fieldName: string, filterIndex: number) => void;

  interface Props {
    min: number;
    max: number;
    type: 'range';
    title: string;
    fieldName: string;
    filterIndex: number;
    onValueChange: ValueChange;
    onErrorChange: (error: boolean, fieldName: string) => void;
    labelType: LabelTypes;
    showHelperText?: boolean;
    selection?: RangeProps;
  }
}

export const hasRangeSelection = (selection: RangeTypes.RangeProps, filter: RangeTypes.Props) =>
  (filter.min !== Number(selection.min) && !!selection.min) ||
  (filter.max !== Number(selection.max) && !!selection.max);

const applicationRangeSchema = zod.object({
  minValue: zod.string(),
  maxValue: zod.string(),
});

export type ApplicationRangeFormData = zod.infer<typeof applicationRangeSchema>;

export const Range: React.FC<RangeTypes.Props> = (props) => {
  const { defaultMask } = labelTypesMask[props.labelType];

  const formEdit = useForm({
    resolver: zodResolver(applicationRangeSchema),
    mode: 'onChange',
    defaultValues: {
      minValue: defaultMask(props.selection?.min || undefined),
      maxValue: defaultMask(props.selection?.max || undefined),
    },
  });

  return (
    <FormProvider {...formEdit}>
      <RangeInputs {...props} />
    </FormProvider>
  );
};

const RangeInputs: React.FC<RangeTypes.Props> = ({
  max: maxProp,
  min: minProp,
  fieldName,
  filterIndex,
  labelType,
  onValueChange,
  onErrorChange,
  title,
  showHelperText = false,
}) => {
  const { mask, defaultMask, unmask } = labelTypesMask[labelType];
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  const { control } = useFormContext<ApplicationRangeFormData>();

  const minValue = useWatch({
    control,
    name: 'minValue',
  });

  const maxValue = useWatch({
    control,
    name: 'maxValue',
  });

  const unmaskedValues = {
    min: unmask(minValue),
    max: unmask(maxValue),
  };

  const validateError = (min: number, max: number) => {
    const minIsBiggerThenMax = min > max;
    if (minIsBiggerThenMax) {
      setErrorMessage('O valor mínimo deve ser menor que o valor máximo');
      return true;
    }

    const minIsLessThenMinProp = min < minProp;
    if (minIsLessThenMinProp) {
      setErrorMessage('O valor mínimo deve ser maior que o valor mínimo permitido');
      return true;
    }

    const maxIsBiggerThenMaxProp = max > maxProp;
    if (maxIsBiggerThenMaxProp) {
      setErrorMessage('O valor máximo deve ser menor que o valor máximo permitido');
      return true;
    }

    setErrorMessage('');
    return false;
  };

  useEffect(() => {
    const hasError = validateError(unmaskedValues.min, unmaskedValues.max);
    if (!hasError) {
      onValueChange({ min: unmaskedValues.min, max: unmaskedValues.max }, fieldName, filterIndex);
    }
    setError(hasError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unmaskedValues.min, unmaskedValues.max]);

  useEffect(() => {
    onErrorChange(error, fieldName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <VStack pb={Spacing['stack.xxxs']}>
      <Text variant="semibold" fontSize={Typography['fontsize.md']}>
        {title}
      </Text>

      <HStack w={'full'} pt={Spacing['stack.xxxs']} alignItems={'center'}>
        <Stack flex={1}>
          <UI.Input
            testId="minValueInput"
            name={'minValue'}
            autoComplete="off"
            control={control as unknown as Control}
            hasShadow={false}
            variant="filled"
            placeholder={`${defaultMask(minProp)}`}
            keyboardType="numeric"
            mask={mask}
          />
          {!!showHelperText && (
            <Text color="neutral.medium" fontSize="xs" pt="stack.quarck">
              Mínimo
            </Text>
          )}
        </Stack>
        <Box width="10px" bg="neutral.medium" h={'1px'} mx={Spacing['inset.nano']} />
        <Stack flex={1}>
          <UI.Input
            testId="maxValueInput"
            name={'maxValue'}
            autoComplete="off"
            control={control as unknown as Control}
            hasShadow={false}
            placeholder={`${defaultMask(maxProp)}`}
            variant="filled"
            keyboardType="numeric"
            mask={mask}
          />
          {!!showHelperText && (
            <Text color="neutral.medium" fontSize="xs" pt="stack.quarck">
              Máximo
            </Text>
          )}
        </Stack>
      </HStack>

      {!!error && (
        <Text
          pt={Spacing['stack.quarck']}
          variant="regular"
          fontSize={Typography['fontsize.xs']}
          color={'feedback.error.default'}>
          {errorMessage}
        </Text>
      )}
    </VStack>
  );
};
